import React, { useState, useCallback, useMemo, useEffect, useLayoutEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';

import { Div, Text, Container, Row, Col, Switch, Tag, Icon } from '@pinpoint/design-kit';
import { charts } from '@pinpoint/onpoint-charts';

import DefaultTheme from 'src/styles/DefaultTheme';
import { getModelTagStyles, formatNumber, noOp } from 'src/functions';
import { Button, Message } from 'src/components';
import CONSTANTS from 'src/store/constants';
import { ModelProp } from 'src/store/typings';
import { CellRegulatoryTag } from 'src/components/models/cells';

const {
  colors: { shade99, tagActuarial, ...rest },
} = DefaultTheme;

function extractNum(data) {
  let num = null;
  if (typeof num === 'string') num = Number(num);
  else if (typeof num === 'object') num = Number(data.value);
  return num;
}

const formatNumberLabelLong = (data) => {
  const num = extractNum(data);
  if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN.toString();
  return num.toFixed(3);
};

const formatNumberLabel = (data) => {
  const num = extractNum(data);
  if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN.toString();
  return num.toFixed(2);
};
const formatPercLabel = (data) => {
  const num = extractNum(data);
  if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN.toString();
  return `${num.toFixed(1)}%`;
};

const formatDollarLabel = (data) => {
  const num = extractNum(data);
  if ([null, undefined].includes(num) || !Number.isFinite(num)) return Number.NaN.toString();
  return `$${formatNumber(Math.round(num))}`;
};

function liftDiffFromMean(lift) {
  return lift >= 1 || lift === 0 ? lift : -1 / lift;
}

export default function ActuarialMode({
  altChart,
  modelView: {
    equalType,
    model: { dataModelType, framework, ...model },
    quantiles,
    quantileIdx,
    availableQuantiles: { current },
  },
  setQuantiles,
  chartMeta: { COLOR: chartColor, SHOW_ACTUAL: showActualProp, HEADER_NAME: headerName, DISPLAY_DOLLAR: displayDollar },
}) {
  const isRemediated = model?.modelData?.metadataPayload?.fairness ?? false;
  const isLegacyActuarial = ['LIFT_VERSION_1_ACT', null, undefined].includes(model?.payload?.version);
  // Chart data extracted using guarded calls to avoid passing nulls or undefined
  const liftScore = useMemo(() => {
    if (!isLegacyActuarial && equalType === null) return [];
    if (isLegacyActuarial) return get(model, `modelData.dataPayload.lift_scores[${quantileIdx}]`);
    if (equalType === 'premiums')
      return (
        model?.modelData?.dataPayload?.equal_premiums?.lift_scores?.[quantileIdx] ||
        model?.modelData?.dataPayload?.equal_premium?.lift_scores?.[quantileIdx]
      );
    if (equalType === 'persons') return get(model, `modelData.dataPayload.equal_persons.lift_scores[${quantileIdx}]`);
    return get(model, `modelData.dataPayload.lift_scores[${quantileIdx}]`);
  }, [model, quantileIdx, isLegacyActuarial, equalType]);
  const pinpointActualLift = useMemo(() => get(liftScore, 'p_actual_lift', []), [liftScore]);
  const pinpointActualValue = useMemo(() => get(liftScore, 'p_actual', []), [liftScore]);
  const pinpointRatioLift = useMemo(() => get(liftScore, 'p_lift', []), [liftScore]);
  const pinpointRatioValue = useMemo(() => get(liftScore, 'p_loss_ratio', []), [liftScore]);
  const diffRatioLift = useMemo(() => [pinpointRatioLift.map(liftDiffFromMean)], [pinpointRatioLift]);
  const diffActualLift = useMemo(() => [pinpointActualLift.map(liftDiffFromMean)], [pinpointActualLift]);
  const disableActual = useMemo(() => isEmpty(pinpointActualValue), [pinpointActualValue]);

  // Toggle Button Initial State
  const [showActual, setShowActual] = useState(showActualProp && !disableActual);
  const [showLift, setShowLift] = useState(altChart);

  // Toggle Button Handlers
  const toggleActual = useCallback(() => setShowActual(!showActual), [showActual]);
  const toggleLift = useCallback(() => setShowLift(!showLift), [showLift]);

  const datumFormatter = useMemo(() => {
    if (showActual && displayDollar && !showLift) return formatDollarLabel; // Actual + Value (severity and loss cost)
    if (!showActual && !showLift) return formatPercLabel; // Ratio + Value (all models)
    if (showLift) return formatNumberLabel; // Actual + Lift, Ratio + Lift (all models)
    return formatNumberLabelLong; // Actual + Value (frequency)
  }, [showLift, showActual, displayDollar]);

  const tooltipFormatter = useCallback(
    ({ dataIndex, seriesIndex }) => {
      const replacePattern = 'YX average';
      const replaceValue = showActual
        ? get(diffActualLift, `[${seriesIndex}][${dataIndex}]`, Number.NaN)
        : get(diffRatioLift, `[${seriesIndex}][${dataIndex}]`, Number.NaN);
      return showLift ? replacePattern.replace('Y', replaceValue.toFixed(2)) : null;
    },
    [showActual, diffActualLift, diffRatioLift, showLift]
  );

  const yAxisFormatter = useCallback(
    (num) => {
      if (displayDollar && showActual && !showLift) return `$${formatNumber(num)}`;
      return num;
    },
    [displayDollar, showActual, showLift]
  );

  const pinpointData = useMemo(() => {
    if (showActual) return showLift ? pinpointActualLift : pinpointActualValue;
    return showLift ? pinpointRatioLift : pinpointRatioValue;
  }, [pinpointActualLift, pinpointActualValue, pinpointRatioLift, pinpointRatioValue, showActual, showLift]);

  const legendInfo = useMemo(
    () => ({
      ...getModelTagStyles(dataModelType, ''),
      ...CONSTANTS.TAL_MODEL.ACT_STYLING[dataModelType],
    }),
    [dataModelType]
  );

  const seriesData = useMemo(
    () => [
      {
        name: 'Pinpoint',
        label: {
          show: true,
          position: 'top',
          color: rest[chartColor],
          fontWeight: 500,
          fontSize: 18,
          formatter: datumFormatter,
        },
        data: pinpointData,
        values: pinpointData,
        symbolSize: 8,
        type: 'line',
        color: rest[chartColor],
        lineStyle: { color: rest[chartColor], width: 3 },
        itemStyle: { color: 'white', borderColor: rest[chartColor], width: 6 },
        markLine: undefined,
        areaStyle: undefined,
      },
    ],
    [chartColor, datumFormatter, pinpointData]
  );

  const yAxisName = useMemo(() => {
    let name = 'Actual ';
    if (showActual) name += `${headerName}`.toLowerCase();
    else name += `client loss ratio`;
    if (showLift) name += ' relativities';
    if (!showActual && !showLift) name += 's';
    return name;
  }, [headerName, showActual, showLift]);

  const bucketSpread = useMemo(() => (pinpointData.at(-1) / pinpointData.at(0)).toFixed(1), [pinpointData]);

  const options = useMemo(
    () => ({
      grid: {
        containLabel: true,
        right: 0,
        left: 0,
        bottom: 0,
      },
      legend: { show: false },
      backgroundColor: false,
      yAxis: {
        show: true,
        name: null,
        axisLabel: {
          fontSize: 14,
          color: shade99,
          fontWeight: 500,
          formatter: yAxisFormatter,
        },
        axisLine: { show: false },
        splitLine: { show: true },
        axisTick: { show: false },
        scale: true,
        splitNumber: 5,
        type: 'value',
      },
      tooltip: {
        formatter: tooltipFormatter,
      },
      xAxis: {
        show: true,
        boundaryGap: ['20%', '20%'],
        minInterval: 1,
        name: null,
        axisLabel: { fontSize: 14, color: shade99, fontWeight: 500 },
        splitLine: { show: false },
        axisLine: { show: true, width: 2, color: shade99 },
        axisTick: { show: false },
        type: 'category',
      },
    }),
    [yAxisFormatter, tooltipFormatter]
  );

  useEffect(() => {
    setShowActual(showActualProp && !disableActual);
  }, [showActualProp, disableActual]);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  if (pinpointData.length === 0) {
    return <Message default type="info" text="Lift Charts unavailable" />;
  }

  return (
    <Container
      rounded="md"
      bg="white"
      data-debug="container"
      maxH="100%"
      h="100%"
      maxW="100%"
      w="100%"
      flexDir="column"
      d="flex"
      flexWrap="nowrap"
    >
      <Row data-debug="title-row" p={{ t: '1.5rem' }} flexWrap="nowrap">
        <Col size={4} data-debug="left-title-col" d="flex" gutterWidth="0" flexWrap="nowrap" justify="left">
          <Text
            textSize="title"
            textColor="tabSelected"
            style={{ fontWeight: 400, whiteSpace: 'nowrap' }}
            p={{ l: '3rem', r: '1.5rem' }}
          >
            Lift Calculations
          </Text>
          {current.map((val) => (
            <Button
              buttonStyle="tab"
              key={`bucket-${val}`}
              selected={quantiles === val}
              p={{ x: '1.5em' }}
              w="1.875rem"
              data-tip={CONSTANTS.TAL_MODEL.QUANTILE_LABEL[val]}
              onClick={() => setQuantiles(val)}
            >
              <Text>{val}</Text>
            </Button>
          ))}
        </Col>
        <Col
          size={4}
          data-debug="center-title-col"
          gutterWidth="0"
          d="flex"
          justify="center"
          align="baseline"
          flexWrap="nowrap"
        >
          <Text textColor={chartColor} textSize="subheader" style={{ whiteSpace: 'nowrap' }}>
            Top-bottom bucket spread: {bucketSpread}x
          </Text>
        </Col>
        <Col size={4} data-debug="right-title-col" gutterWidth="0" flexWrap="nowrap" justify="right" d="flex">
          <Tag
            p="0px"
            h="2rem"
            m={{ r: '.5rem' }}
            whiteSpace="nowrap"
            border="1px solid"
            textColor={chartColor}
            borderColor={chartColor}
            data-tip={legendInfo.tip}
            prefix={
              <Div h="100%" d="flex" align="center" p=".375rem">
                <Icon name={legendInfo.name} viewBox={legendInfo.viewBox} color={chartColor} size="1.25rem" />
              </Div>
            }
            suffix={
              isRemediated ? (
                <Div h="100%" d="flex" align="center" bg={chartColor}>
                  <Icon name="Fairness" size="1.25rem" viewBox="0 0 37 36" color="white" m={{ x: '.375rem' }} />
                </Div>
              ) : undefined
            }
          >
            <Text textSize="subheader" m={{ x: '.375rem' }}>
              {CONSTANTS.SEGMENT.TAG[dataModelType] || CONSTANTS.TAL_MODEL.TAG_NAME[dataModelType]}
            </Text>
          </Tag>
          <CellRegulatoryTag
            h="2rem"
            m={{ r: '3rem' }}
            textSize="subheader"
            iconProps={{ size: '1.25rem' }}
            value={{ framework }}
          />
        </Col>
      </Row>
      <Row data-debug="chart-row" flexGrow="1" m={{ r: '2rem' }}>
        <Col
          data-debug="yaxis-col"
          gutterWidth="0"
          transform="rotate(180deg)"
          style={{ writingMode: 'vertical-lr', fontSize: '18px' }}
          d="flex"
          align="center"
          justify="center"
          textColor="shade99"
        >
          <Div tag="span" m={{ r: '2rem' }} p={{ l: '1rem' }}>
            {yAxisName}
          </Div>
        </Col>
        <Col data-debug="chart-col" gutterWidth="0" d="flex" flexGrow="1">
          <charts.Line
            overflow="hidden"
            usePalette={false}
            showMarkline={false}
            showGradients={false}
            labels={[...Array(quantiles).keys()].map((k) => k + 1)}
            series={seriesData}
            options={options}
          />
        </Col>
      </Row>
      <Row data-debug="spacer2" m={{ x: '0rem' }} p={{ x: '2rem' }}>
        <Col
          size={12}
          gutterWidth="0"
          data-debug="xaxis--col"
          justify="center"
          align="top"
          d="flex"
          textColor="shade99"
          style={{ fontSize: '18px' }}
        >
          <Div tag="span" p={{ y: '1rem' }}>
            Ranking percentiles with equal {isLegacyActuarial ? 'premiums' : `${equalType}`}
          </Div>
        </Col>
        <Col
          size={12}
          gutterWidth="0"
          data-debug="left-toggles-col"
          borderColor="shadeE6"
          border={{ t: '1px solid' }}
        />
      </Row>
      <Row data-debug="toggle-row" p={{ t: '1rem', b: '1.5rem' }}>
        <Col
          size={4}
          data-debug="left-toggle-col"
          gutterWidth="6rem"
          d="flex"
          justify="left"
          align="baseline"
          flexWrap="nowrap"
        >
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor={disableActual || showActual ? 'shade99' : chartColor}
            style={{ whiteSpace: 'nowrap' }}
          >
            Actuals: Loss ratio
          </Text>
          <Div disabled alignSelf="center" m={{ x: '1rem' }} onClick={disableActual ? noOp : toggleActual}>
            <Switch
              m="0"
              checked={showActual}
              activeColor={disableActual ? 'shade99' : chartColor}
              inactiveColor={disableActual ? 'shade99' : chartColor}
              activeShadow="0"
              inactiveShadow="0"
            />
          </Div>
          <Text
            h="1.125rem"
            textSize="subtitle"
            textColor={disableActual || !showActual ? 'shade99' : chartColor}
            style={{ whiteSpace: 'nowrap' }}
          >
            {headerName}
          </Text>
        </Col>
        <Col size={4} />
        <Col
          size={4}
          data-debug="right-toggles-col"
          gutterWidth="6rem"
          d="flex"
          justify="right"
          align="baseline"
          flexWrap="nowrap"
        >
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor={showLift ? 'shade99' : chartColor}
            style={{ whiteSpace: 'nowrap' }}
          >
            Values
          </Text>
          <Div alignSelf="center" m={{ x: '1rem' }} onClick={toggleLift}>
            <Switch
              m="0"
              checked={showLift}
              activeColor={chartColor}
              inactiveColor={chartColor}
              activeShadow="0"
              inactiveShadow="0"
            />
          </Div>
          <Text
            textSize="subtitle"
            h="1.125rem"
            textColor={showLift ? chartColor : 'shade99'}
            style={{ whiteSpace: 'nowrap' }}
          >
            Lift
          </Text>
        </Col>
      </Row>
    </Container>
  );
}

ActuarialMode.defaultProps = {
  chartMeta: {
    TITLE: '',
    COLOR: 'black',
    SHOW_ACTUAL: false,
    HEADER_NAME: '',
  },
};

ActuarialMode.propTypes = {
  altChart: PropTypes.bool.isRequired,
  modelView: PropTypes.shape({
    availableQuantiles: PropTypes.shape({
      current: PropTypes.arrayOf(PropTypes.number).isRequired,
    }).isRequired,
    quantiles: PropTypes.number.isRequired,
    quantileIdx: PropTypes.number.isRequired,
    equalType: PropTypes.string.isRequired,
    model: ModelProp,
  }).isRequired,
  setQuantiles: PropTypes.func.isRequired,
  chartMeta: PropTypes.shape({
    TITLE: PropTypes.string,
    COLOR: PropTypes.string,
    SHOW_ACTUAL: PropTypes.bool,
    DISPLAY_DOLLAR: PropTypes.bool,
    HEADER_NAME: PropTypes.string,
  }),
};
